import React, { useState, useMemo, useEffect, useRef } from 'react';
import api from '../api/axios';

const COLORS = {
  bg: '#0A0A0A',
  surface: '#171717',
  surfaceCard: '#1C1C1C',
  text: '#FFFFFF',
  textLight: '#A3A3A3',
  textMuted: '#737373',
  accent: '#F59E0B',
  accentLight: '#FCD34D',
  accentDark: '#D97706',
  border: '#272727',
  borderAccent: 'rgba(245, 158, 11, 0.2)',
  glow: 'rgba(245, 158, 11, 0.1)',
};

const FONT_DISPLAY = '"Cormorant Garamond", "Playfair Display", Georgia, serif';
const FONT_BODY = '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const FONT_LUXURY = '"Cormorant Garamond", Georgia, serif';

const CatalogGlass = () => {
  const [glasses, setGlasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMarca, setSelectedMarca] = useState('');
  const [selectedColeccion, setSelectedColeccion] = useState('');
  const [loading, setLoading] = useState(true);
  const tabsRef = useRef(null);
  const carouselRef = useRef(null);
  const autoScrollRef = useRef(null);

  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

    useEffect(() => {
    // 🚀 OPTIMIZACIÓN: Pedimos solo 100 productos al backend.
    // La base de datos hará el trabajo pesado, no tu red ni tu navegador.
    api.get('/glasses?limit=100')
      .then(res => {
        const glassesData = Array.isArray(res.data.data) ? res.data.data : [];
        const mapped = glassesData.map(g => ({
          ...g,
          imagenFrontal: g.imagen_frontal,
          imagenLateral: g.imagen_lateral
        }));
        setGlasses(mapped);
      })
      .catch(err => console.error('Error al obtener gafas:', err))
      .finally(() => setLoading(false));
  }, []);

  const destacados = useMemo(() => {
    if (!glasses.length) return [];
    return shuffle(glasses).slice(0, 3);
  }, [glasses]);

  const marcasConConteo = useMemo(() => {
    const map = new Map();
    glasses.forEach(g => {
      if (g.marca) {
        map.set(g.marca, (map.get(g.marca) || 0) + 1);
      }
    });
    return Array.from(map.entries())
      .map(([marca, count]) => ({ marca, count }))
      .sort((a, b) => a.marca.localeCompare(b.marca));
  }, [glasses]);
  const coleccionesConConteo = useMemo(() => {
    const map = new Map();
    glasses.forEach(g => {
      if (g.coleccion) {
        map.set(g.coleccion, (map.get(g.coleccion) || 0) + 1);
      }
    });
    return Array.from(map.entries())
      .map(([coleccion, count]) => ({ coleccion, count }))
      .sort((a, b) => a.coleccion.localeCompare(b.coleccion));
  }, [glasses]);
  const filteredGlasses = useMemo(() => {
    return glasses.filter(glass => {
      const matchSearch = glass.modelo?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchMarca = !selectedMarca || glass.marca === selectedMarca;
      const matchColeccion = !selectedColeccion || glass.coleccion === selectedColeccion;
      return matchSearch && matchMarca && matchColeccion;
    });
  }, [glasses, searchTerm, selectedMarca, selectedColeccion]);

  // 🚀 OPTIMIZACIÓN: Limitamos estrictamente a 20 productos para el carrusel
  const carouselProducts = useMemo(() => {
    if (!filteredGlasses.length) return [];
    return shuffle(filteredGlasses).slice(0, 20); 
  }, [filteredGlasses]);

  const [activeIdx, setActiveIdx] = useState(0);
  
  // 🆕 Estados para los indicadores del carrusel de productos
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(2);
  const [totalPages, setTotalPages] = useState(1);

  // 🆕 Calcular cuántos productos caben por página según el ancho visible
  useEffect(() => {
    const calculatePages = () => {
      const container = carouselRef.current;
      if (!container || carouselProducts.length === 0) return;

      const firstCard = container.firstElementChild;
      if (!firstCard) return;

      const cardWidth = firstCard.getBoundingClientRect().width;
      const gap = window.innerWidth >= 768 ? 24 : 16; // md:gap-6 vs gap-4
      const visibleWidth = container.clientWidth;
      
      // Cuántas tarjetas completas caben en el viewport
      const visible = Math.floor((visibleWidth + gap) / (cardWidth + gap));
      const perPage = Math.max(1, visible);
      
      setItemsPerPage(perPage);
      setTotalPages(Math.ceil(carouselProducts.length / perPage));
    };

    calculatePages();
    window.addEventListener('resize', calculatePages);
    return () => window.removeEventListener('resize', calculatePages);
  }, [carouselProducts]);

  // 🆕 Detectar la página actual mientras el usuario hace scroll
  useEffect(() => {
    const container = carouselRef.current;
    if (!container || itemsPerPage === 0) return;

    const handleScroll = () => {
      const firstCard = container.firstElementChild;
      if (!firstCard) return;
      const cardWidth = firstCard.getBoundingClientRect().width;
      const gap = window.innerWidth >= 768 ? 24 : 16;
      const step = cardWidth + gap;
      const page = Math.round(container.scrollLeft / (step * itemsPerPage));
      setCurrentPage(Math.min(page, totalPages - 1));
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [itemsPerPage, totalPages, carouselProducts]);

  // 🆕 Función para saltar a una página específica al hacer clic en un punto
  const goToPage = (pageIndex) => {
    const container = carouselRef.current;
    if (!container) return;
    const firstCard = container.firstElementChild;
    if (!firstCard) return;
    const cardWidth = firstCard.getBoundingClientRect().width;
    const gap = window.innerWidth >= 768 ? 24 : 16;
    const step = cardWidth + gap;
    
    container.scrollTo({
      left: step * itemsPerPage * pageIndex,
      behavior: 'smooth',
    });
  };

  // Auto-rotación del carrusel de destacados
  useEffect(() => {
    if (destacados.length === 0) return;
    const id = setInterval(() => {
      setActiveIdx(i => (i + 1) % destacados.length);
    }, 4500);
    return () => clearInterval(id);
  }, [destacados]);

  const nextSlide = () => setActiveIdx(i => (i + 1) % destacados.length);
  const prevSlide = () => setActiveIdx(i => (i - 1 + destacados.length) % destacados.length);

  useEffect(() => {
    if (!tabsRef.current) return;
    const activeTab = tabsRef.current.querySelector('[data-active="true"]');
    if (activeTab) {
      activeTab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [selectedMarca, selectedColeccion]);

  useEffect(() => {
  if (carouselRef.current) {
    carouselRef.current.scrollTo({ left: 0, behavior: 'smooth' });
  }
  }, [selectedColeccion, selectedMarca, searchTerm]);

  const scrollCarousel = (direction) => {
    const container = carouselRef.current;
    if (!container) return;
    const firstCard = container.firstElementChild;
    if (!firstCard) return;
    const cardWidth = firstCard.getBoundingClientRect().width;
    const gap = 24;
    container.scrollBy({
      left: direction * (cardWidth + gap) * 5,
      behavior: 'smooth',
    });
  };

  // Auto-scroll del carrusel de productos
  // Auto-scroll del carrusel de productos (modificado)
    useEffect(() => {
      if (carouselProducts.length === 0) return;
      const container = carouselRef.current;
      if (!container) return;

      const startAutoScroll = () => {
        autoScrollRef.current = setInterval(() => {
          const firstCard = container.firstElementChild;
          if (!firstCard) return;
          const cardWidth = firstCard.getBoundingClientRect().width;
          const gap = window.innerWidth >= 768 ? 24 : 16;
          const step = cardWidth + gap;

          const maxScroll = container.scrollWidth - container.clientWidth;
          if (container.scrollLeft >= maxScroll - 5) {
            container.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            // 🆕 Avanzar de página en página en lugar de tarjeta por tarjeta
            container.scrollBy({ left: step * itemsPerPage, behavior: 'smooth' });
          }
        }, 3500);
      };

      startAutoScroll();

      const pause = () => clearInterval(autoScrollRef.current);
      const resume = () => {
        clearInterval(autoScrollRef.current);
        startAutoScroll();
      };

      container.addEventListener('mouseenter', pause);
      container.addEventListener('mouseleave', resume);
      container.addEventListener('touchstart', pause);
      container.addEventListener('touchend', resume);

      return () => {
        clearInterval(autoScrollRef.current);
        container.removeEventListener('mouseenter', pause);
        container.removeEventListener('mouseleave', resume);
        container.removeEventListener('touchstart', pause);
        container.removeEventListener('touchend', resume);
      };
    }, [carouselProducts, itemsPerPage]); // 🆕 itemsPerPage añadido a dependencias

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: COLORS.bg, color: COLORS.text, fontFamily: FONT_BODY }}
      >
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
          <span className="text-sm tracking-widest uppercase text-neutral-400">Cargando catálogo...</span>
        </div>
      </div>
    );
  }

  return (
    <div

      // Revisión de sección para depurar
      id="Catalog"
      style={{ background: COLORS.bg, color: COLORS.text, fontFamily: FONT_BODY }}
      className=""
    >
      {/*<div className="absolute -top-40 -right-40 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />*/}
      {/*<div className="absolute -bottom-40 -left-40 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />*/}

      {!loading && destacados.length > 0 && (
        <>
          <style>{`
            @keyframes slideUpFromBottom {
              0%   { opacity: 0; transform: translate(-50%, 140%); filter: blur(6px); }
              60%  { opacity: 1; filter: blur(0px); }
              100% { opacity: 1; transform: translate(-50%, 0); filter: blur(0px); }
            }
            .scrollbar-hide::-webkit-scrollbar { display: none; }
          `}</style>
{/*Revisión para homologar los div en los componentes*/}
            <div className="relative w-full h-[80vh]">
              <div className="overflow-hidden rounded-2xl h-full w-full">
                <div
                  className="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] h-full"
                  style={{ transform: `translateX(-${activeIdx * 100}%)` }}
                >
                  {destacados.map((glass) => (
                    <div key={`dest-${glass.id}`} className="min-w-full h-full">
                      <div
                        className="grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden border h-full w-full"
                        style={{ background: COLORS.surfaceCard, borderColor: COLORS.borderAccent }}
                      >
                        <div className="relative overflow-hidden bg-neutral-800 h-full w-full">
                          <img
                            src={glass.imagenFrontal}
                            alt={glass.modelo}
                            className="w-full h-full object-cover"
                          />
                          <div
                            className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase font-bold text-neutral-900"
                            style={{ background: 'linear-gradient(135deg, #FCD34D, #F59E0B)' }}
                          >
                            Destacado
                          </div>
                        </div>

                        <div className="p-4 md:p-6 flex flex-col justify-center h-full">
                          <p style={{ color: COLORS.accent }} className="text-[10px] tracking-[0.2em] uppercase mb-2 font-semibold">
                            {glass.marca}
                          </p>
                          <h3
                            style={{ fontFamily: FONT_LUXURY, color: COLORS.text, letterSpacing: '0.02em' }}
                            className="text-xl md:text-2xl leading-tight mb-1.5 font-normal"
                          >
                            {glass.modelo}
                          </h3>
                          <p style={{ color: COLORS.textMuted }} className="text-xs mb-4 tracking-wide">
                            {glass.coleccion}
                          </p>
                          <p
                            style={{ fontFamily: FONT_LUXURY, color: COLORS.accent }}
                            className="text-xl md:text-2xl font-medium tracking-wide mb-5"
                          >
                            ${Number(glass.precio || 0).toFixed(2)}
                          </p>
                          <span className="text-[10px] text-amber-400/70 hover:text-amber-400 transition-colors tracking-[0.25em] uppercase cursor-pointer">
                            Ver más →
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={prevSlide}
                aria-label="Anterior"
                className="absolute left-2 md:-left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 z-10 text-sm"
                style={{ background: 'rgba(10,10,10,0.7)', border: `1px solid ${COLORS.borderAccent}`, color: COLORS.accent, backdropFilter: 'blur(8px)' }}
              >
                ‹
              </button>
              <button
                onClick={nextSlide}
                aria-label="Siguiente"
                className="absolute right-2 md:-right-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 z-10 text-sm"
                style={{ background: 'rgba(10,10,10,0.7)', border: `1px solid ${COLORS.borderAccent}`, color: COLORS.accent, backdropFilter: 'blur(8px)' }}
              >
                ›
              </button>
            </div>

            <div className="flex justify-center gap-2 mt-4">
              {destacados.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIdx(i)}
                  aria-label={`Ir al destacado ${i + 1}`}
                  className="h-1 rounded-full transition-all duration-500"
                  style={{
                    width: i === activeIdx ? 28 : 6,
                    background: i === activeIdx ? COLORS.accent : COLORS.border,
                  }}
                />
              ))}
            </div>
        </>
      )}

      <div className="max-w-[1400px] mx-auto px-6 md:px-8 relative z-10">
        {/* ───── PESTAÑAS DE COLECCIONES ───── */}
      {coleccionesConConteo.length > 0 && (
        <div
          ref={tabsRef}
          className="flex gap-2 md:gap-3 overflow-x-auto pb-5 mb-6 scrollbar-hide snap-x"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* Pestaña "Todas" */}
          <button
            data-active={selectedColeccion === ''}
            onClick={() => setSelectedColeccion('')}
            className="flex-shrink-0 snap-start px-4 md:px-5 py-2 rounded-full border text-[10px] md:text-[11px] tracking-[0.15em] uppercase font-semibold transition-all duration-300 whitespace-nowrap"
            style={{
              background:
                selectedColeccion === ''
                  ? 'linear-gradient(135deg, #FCD34D, #F59E0B)'
                  : COLORS.surfaceCard,
              borderColor:
                selectedColeccion === '' ? 'transparent' : COLORS.border,
              color: selectedColeccion === '' ? '#0A0A0A' : COLORS.textLight,
            }}
          >
            Todas
            <span
              className="ml-2 text-[9px] opacity-70"
              style={{ fontFamily: FONT_BODY }}
            >
              {glasses.length}
            </span>
          </button>

          {/* Pestañas de colecciones */}
          {coleccionesConConteo.map(({ coleccion, count }) => {
            const isActive = selectedColeccion === coleccion;
            return (
              <button
                key={coleccion}
                data-active={isActive}
                onClick={() => setSelectedColeccion(coleccion)}
                className="flex-shrink-0 snap-start px-4 md:px-5 py-2 rounded-full border text-[10px] md:text-[11px] tracking-[0.15em] uppercase font-semibold transition-all duration-300 whitespace-nowrap hover:border-amber-500/40"
                style={{
                  background: isActive
                    ? 'linear-gradient(135deg, #FCD34D, #F59E0B)'
                    : COLORS.surfaceCard,
                  borderColor: isActive ? 'transparent' : COLORS.border,
                  color: isActive ? '#0A0A0A' : COLORS.textLight,
                }}
              >
                {coleccion}
                <span
                  className="ml-2 text-[9px] opacity-70"
                  style={{ fontFamily: FONT_BODY }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}
        <div className="mb-10">
          <div
            className="h-[1px] w-full mt-1"
            style={{
              background: `linear-gradient(90deg, transparent, ${COLORS.borderAccent}, transparent)`
            }}
          />
        </div>

        <p
          style={{ color: COLORS.textLight, fontFamily: FONT_BODY }}
          className="text-xs tracking-[0.15em] uppercase mb-8 border-b border-neutral-800/50 pb-4"
        >
          {filteredGlasses.length} producto{filteredGlasses.length !== 1 ? 's' : ''}
          {selectedMarca && (
            <span className="text-amber-400 ml-2">· {selectedMarca}</span>
          )}
        </p>

        {carouselProducts.length === 0 ? (
          <div style={{ color: COLORS.textLight, fontFamily: FONT_BODY }} className="text-center py-20">
            <p className="text-sm tracking-widest uppercase">No hay productos que coincidan con los filtros</p>
          </div>
        ) : (
          <div className="relative">
            <button
              onClick={() => scrollCarousel(-1)}
              aria-label="Anterior"
              className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center transition-all duration-300 hover:scale-110 z-20 text-lg"
              style={{
                background: 'rgba(10,10,10,0.85)',
                border: `1px solid ${COLORS.borderAccent}`,
                color: COLORS.accent,
                backdropFilter: 'blur(8px)',
              }}
            >
              ‹
            </button>

            <button
              onClick={() => scrollCarousel(1)}
              aria-label="Siguiente"
              className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center transition-all duration-300 hover:scale-110 z-20 text-lg"
              style={{
                background: 'rgba(10,10,10,0.85)',
                border: `1px solid ${COLORS.borderAccent}`,
                color: COLORS.accent,
                backdropFilter: 'blur(8px)',
              }}
            >
              ›
            </button>
            <div
              ref={carouselRef}
              className="flex gap-4 md:gap-6 overflow-x-auto scroll-smooth pb-4 snap-x snap-mandatory scrollbar-hide"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {carouselProducts.map((glass) => (
                <div
                  key={glass.id}
                  className="flex-shrink-0 snap-start w-[calc(50%-0.5rem)] sm:w-[calc(50%-0.75rem)] md:w-[calc(33.333%-1rem)] lg:w-[calc(20%-1.2rem)]"
                >
                  <div
                    style={{
                      background: COLORS.surfaceCard,
                      borderColor: COLORS.border
                    }}
                    className="border rounded-xl overflow-hidden group transition-all duration-500 hover:border-amber-500/20 hover:shadow-2xl hover:shadow-amber-500/5 h-full"
                  >
                    <div className="relative overflow-hidden bg-neutral-800 aspect-square">
                      <img
                        src={glass.imagenFrontal}
                        alt={glass.modelo}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/60 via-transparent to-transparent pointer-events-none" />
                    </div>
                    
                    <div className="p-3 md:p-5">
                      <p
                        style={{ color: COLORS.accent, fontFamily: FONT_BODY }}
                        className="text-[9px] sm:text-[10px] tracking-[0.15em] uppercase mb-1.5 font-semibold line-clamp-1"
                      >
                        {glass.marca}
                      </p>
                      <h3
                        style={{ fontFamily: FONT_LUXURY, color: COLORS.text, letterSpacing: '0.02em' }}
                        className="text-base sm:text-lg md:text-xl leading-tight mb-1.5 font-normal line-clamp-1"
                      >
                        {glass.modelo}
                      </h3>
                      <p
                        style={{ color: COLORS.textMuted, fontFamily: FONT_BODY }}
                        className="text-[10px] sm:text-[11px] mb-2.5 tracking-wide line-clamp-1"
                      >
                        {glass.coleccion}
                      </p>
                      <div className="flex items-center justify-between pt-2.5 border-t border-neutral-800/50">
                        <p
                          style={{ fontFamily: FONT_LUXURY, color: COLORS.accent }}
                          className="text-base sm:text-xl font-medium tracking-wide"
                        >
                          ${Number(glass.precio || 0).toFixed(2)}
                        </p>
                        <span className="text-[9px] sm:text-[10px] text-amber-400/60 group-hover:text-amber-400 transition-colors duration-300 tracking-widest">
                          Ver →
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* INDICADORES DE NAVEGACIÓN */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToPage(i)}
                    aria-label={`Ir a la página ${i + 1}`}
                    className="h-1 rounded-full transition-all duration-500 hover:scale-110"
                    style={{
                      width: i === currentPage ? 24 : 6,
                      background: i === currentPage ? COLORS.accent : COLORS.border,
                      opacity: i === currentPage ? 1 : 0.6,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CatalogGlass;